import { createContext, useContext } from "react";
import { useModels as useModelsHook } from "../hooks/useModels";
import { getPublicFileUrl } from "../utils/storageHelpers";

const ModelsContext = createContext();

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
const formatFileSize = (bytes) => {
  if (!bytes) return "N/A";
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} Mo`;
};

/**
 * Supported download formats in display order (XKT excluded - for display only)
 */
const SUPPORTED_FORMATS = ['dwg', 'ifc', 'rfa', 'rvt'];

export function ModelsProvider({ children }) {
  const modelsData = useModelsHook();

  // Transform models for backward compatibility
  const transformedModels = modelsData.models.map((model) => {
    // Build available files from new model_files structure or legacy fields
    const availableFiles = {};
    
    if (model.model_files) {
      // New multi-format structure - process in defined order
      for (const format of SUPPORTED_FORMATS) {
        const data = model.model_files[format];
        if (data && data.url) {
          availableFiles[format] = {
            url: getPublicFileUrl(data.url),
            size: data.size,
            sizeFormatted: formatFileSize(data.size),
          };
        }
      }
    } else if (model.model_ifc_url) {
      // Legacy single IFC format
      availableFiles.ifc = {
        url: getPublicFileUrl(model.model_ifc_url),
        size: model.model_ifc_size,
        sizeFormatted: formatFileSize(model.model_ifc_size),
      };
    }

    // Get available format types (maintaining order)
    const availableFormats = SUPPORTED_FORMATS.filter(f => availableFiles[f]);
    const primaryFormat = availableFormats[0] || "ifc";
    const primaryFile = availableFiles[primaryFormat];

    return {
      id: model.model_id || model.id,
      name: model.model_name || model.name || "Sans nom",
      category: model.model_category || model.category || "Ferronnerie",
      description: model.model_description || model.description || "",
      xktFile: model.model_xkt_url
        ? getPublicFileUrl(model.model_xkt_url)
        : model.xktFile || "",
      // Mockup images for carousel display (used when no XKT)
      mockupImages: (model.mockup_images || []).map(img => ({
        url: getPublicFileUrl(img.url),
        size: img.size,
      })),
      // Multi-format support
      availableFiles,
      availableFormats,
      // Legacy support - use primary file
      downloadFile: primaryFile?.url || "",
      downloadType: primaryFormat,
      ifcFile: availableFiles.ifc?.url || primaryFile?.url || "",
      fileSize: model.model_xkt_size
        ? formatFileSize(model.model_xkt_size)
        : model.fileSize || "N/A",
      downloads: model.downloads || 0,
      downloadsByFormat: model.downloads_by_format || {},
      featured: model.featured || false,
    };
  });

  const contextValue = {
    ...modelsData,
    models: transformedModels,
  };

  return (
    <ModelsContext.Provider value={contextValue}>
      {children}
    </ModelsContext.Provider>
  );
}

export function useModels() {
  const context = useContext(ModelsContext);
  if (!context) {
    throw new Error("useModels must be used within ModelsProvider");
  }
  return context;
}
